import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import QRCode from 'qrcode';
import { 
  createGroup, 
  getUserGroups, 
  getGroupMembers,
  generateGroupInvite,
  joinGroupByInvite,
  leaveGroup,
  // Pictures feature disabled - use group chat instead
  // getGroupPictures,
  // getGroupPictureUrl,
  // uploadGroupPicture
} from '../services/groupService';
import type { Group, GroupMember } from '../services/groupService';

import '../styles/groups.css';

const GroupManagement: React.FC = () => {
  // State variables
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [userGroups, setUserGroups] = useState<Group[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [groupMembers, setGroupMembers] = useState<GroupMember[]>([]);
  const [activeTab, setActiveTab] = useState<'info' | 'members'>('info');
  // Pictures feature disabled - use group chat instead
  // const [picturesList, setPicturesList] = useState<any[]>([]);
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  
  // Form states
  const [newGroupName, setNewGroupName] = useState<string>('');
  const [newGroupDesc, setNewGroupDesc] = useState<string>('');
  const [inviteCode, setInviteCode] = useState<string>('');
  const [joinInviteCode, setJoinInviteCode] = useState<string>('');
  // Pictures feature disabled - use group chat instead
  // const [selectedFile, setSelectedFile] = useState<File | null>(null);
  // const [pictureDescription, setPictureDescription] = useState<string>('');
  
  // Ref for QR code canvas
  const qrCanvasRef = useRef<HTMLCanvasElement>(null);
  
  // Status/error states
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isCreatingGroup, setIsCreatingGroup] = useState<boolean>(false);
  const [isJoiningGroup, setIsJoiningGroup] = useState<boolean>(false);
  const [isGeneratingInvite, setIsGeneratingInvite] = useState<boolean>(false);
  // Pictures feature disabled - use group chat instead
  // const [isUploadingPicture, setIsUploadingPicture] = useState<boolean>(false);
  
  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  
  // Load user's groups on component mount
  useEffect(() => {
    loadUserGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount
  
  // Load group details when a group is selected
  useEffect(() => {
    if (selectedGroupId) {
      loadGroupDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroupId, activeTab]); // Only re-run when these change
  
  // Generate QR code when invite code changes
  useEffect(() => {
    if (inviteCode && qrCanvasRef.current) {
      const inviteUrl = `${window.location.origin}/invite#${inviteCode}`;
      QRCode.toCanvas(qrCanvasRef.current, inviteUrl, {
        width: 200,
        margin: 2,
        color: {
          dark: '#003366', // TU Braunschweig blue
          light: '#FFFFFF'
        }
      }).catch((error) => {
        console.error('Error generating QR code:', error);
      });
    }
  }, [inviteCode]);
  
  // Load user's groups
  const loadUserGroups = async () => {
    setIsLoading(true);
    try {
      const groups = await getUserGroups();
      setUserGroups(groups);
      
      // If there's at least one group, select it
      if (groups.length > 0 && !selectedGroupId) {
        setSelectedGroupId(groups[0].id);
      }
    } catch (error) {
      console.error('Error loading user groups:', error);
      setError('Fehler beim Laden der Gruppen. Bitte versuche es später erneut.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Load details for the selected group
  const loadGroupDetails = async () => {
    if (!selectedGroupId) return;
    
    setIsLoading(true);
    try {
      // Load group members
      if (activeTab === 'members') {
        const members = await getGroupMembers(selectedGroupId);
        setGroupMembers(members);
      }
      
      // Pictures feature disabled - use group chat instead
      // Load group pictures
      // if (activeTab === 'pictures') {
      //   const pictures = await getGroupPictures(selectedGroupId);
      //   setPicturesList(pictures);
      // }
    } catch (error) {
      console.error('Error loading group details:', error);
      setError('Fehler beim Laden der Gruppendetails.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Create a new group
  const handleCreateGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    
    if (!newGroupName.trim()) {
      setError('Bitte gib einen Gruppennamen ein.');
      return;
    }
    
    // Check if offline
    if (!navigator.onLine) {
      setError('Keine Internetverbindung. Gruppenaktionen erfordern eine Online-Verbindung.');
      return;
    }
    
    setIsCreatingGroup(true);
    try {
      const result = await createGroup(newGroupName, newGroupDesc || null);
      
      if (result.success) {
        setSuccessMessage('Gruppe erfolgreich erstellt!');
        setNewGroupName('');
        setNewGroupDesc('');
        await loadUserGroups();
        setSelectedGroupId(result.group_id!);
      } else {
        setError(result.error || 'Fehler beim Erstellen der Gruppe.');
      }
    } catch (error) {
      console.error('Error creating group:', error);
      setError('Fehler beim Erstellen der Gruppe. Bitte versuche es später erneut.');
    } finally {
      setIsCreatingGroup(false);
    }
  };
  
  // Join a group with an invite code
  const handleJoinGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    
    if (!joinInviteCode.trim()) {
      setError('Bitte gib einen Einladungscode ein.');
      return;
    }
    
    // Check if offline
    if (!navigator.onLine) {
      setError('Keine Internetverbindung. Gruppenaktionen erfordern eine Online-Verbindung.');
      return;
    }
    
    setIsJoiningGroup(true);
    try {
      const result = await joinGroupByInvite(joinInviteCode);
      
      if (result.success) {
        setSuccessMessage('Gruppe erfolgreich beigetreten!');
        setJoinInviteCode('');
        await loadUserGroups();
        setSelectedGroupId(result.group_id!);
      } else {
        setError(result.error || 'Fehler beim Beitreten der Gruppe.');
      }
    } catch (error) {
      console.error('Error joining group:', error);
      setError('Fehler beim Beitreten der Gruppe. Bitte versuche es später erneut.');
    } finally {
      setIsJoiningGroup(false);
    }
  };
  
  // Generate an invite code for the selected group
  const handleGenerateInvite = async () => {
    if (!selectedGroupId) return;
    
    // Check if offline
    if (!navigator.onLine) {
      setError('Keine Internetverbindung. Gruppenaktionen erfordern eine Online-Verbindung.');
      return;
    }
    
    setError(null);
    setSuccessMessage(null);
    setIsGeneratingInvite(true);
    
    try {
      const result = await generateGroupInvite(selectedGroupId);
      
      if (result.success) {
        setInviteCode(result.code!);
      } else {
        setError(result.error || 'Fehler beim Generieren des Einladungscodes.');
      }
    } catch (error) {
      console.error('Error generating invite:', error);
      setError('Fehler beim Generieren des Einladungscodes.');
    } finally {
      setIsGeneratingInvite(false);
    }
  };
  
  // Leave the selected group
  const handleLeaveGroup = async () => {
    if (!selectedGroupId) return;
    
    // Check if offline
    if (!navigator.onLine) {
      setError('Keine Internetverbindung. Gruppenaktionen erfordern eine Online-Verbindung.');
      return;
    }
    
    if (!window.confirm('Bist du sicher, dass du die Gruppe verlassen möchtest?')) {
      return;
    }
    
    setIsLoading(true);
    try {
      const result = await leaveGroup(selectedGroupId);
      
      if (result.success) {
        setSuccessMessage('Gruppe erfolgreich verlassen!');
        setSelectedGroupId(null);
        await loadUserGroups();
      } else {
        setError(result.error || 'Fehler beim Verlassen der Gruppe.');
      }
    } catch (error) {
      console.error('Error leaving group:', error);
      setError('Fehler beim Verlassen der Gruppe.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Pictures feature disabled - use group chat instead
  /*
  // Handle file selection for picture upload
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
  };
  
  // Upload a picture for the selected group
  const handleUploadPicture = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedGroupId || !selectedFile) {
      setError('Bitte wähle eine Datei aus.');
      return;
    }
    
    setIsUploadingPicture(true);
    try {
      const result = await uploadGroupPicture(
        selectedGroupId, 
        selectedFile, 
        pictureDescription || null
      );
      
      if (result.success) {
        setSuccessMessage('Bild erfolgreich hochgeladen!');
        setSelectedFile(null);
        setPictureDescription('');
        
        // Reset the file input
        const fileInput = document.getElementById('picture-upload') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
        
        // Reload pictures
        loadGroupDetails();
      } else {
        setError(result.error || 'Fehler beim Hochladen des Bildes.');
      }
    } catch (error) {
      console.error('Error uploading picture:', error);
      setError('Fehler beim Hochladen des Bildes.');
    } finally {
      setIsUploadingPicture(false);
    }
  };
  */
  
  // Copy invite code to clipboard
  const copyInviteToClipboard = () => {
    const inviteUrl = `${window.location.origin}/invite#${inviteCode}`;
    navigator.clipboard.writeText(inviteUrl);
    setSuccessMessage('Einladungslink in die Zwischenablage kopiert!');
    setTimeout(() => setSuccessMessage(null), 2000);
  };
  
  // Get the selected group object
  const selectedGroup = userGroups.find(group => group.id === selectedGroupId);
  
  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  return (
    <div className="group-management">
      <div className="group-header">
        <Link to="/" className="back-button">← Zurück zur Rätseljagd</Link>
        <h2>Gruppenmanagement</h2>
      </div>
      
      {/* Show offline warning */}
      {!isOnline && (
        <div className="offline-warning">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="1" y1="1" x2="23" y2="23"></line>
            <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55"></path>
            <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39"></path>
            <path d="M10.71 5.05A16 16 0 0 1 22.58 9"></path>
            <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88"></path>
            <path d="M8.53 16.11a6 6 0 0 1 6.95 0"></path>
            <line x1="12" y1="20" x2="12.01" y2="20"></line>
          </svg>
          <span>Keine Internetverbindung - Gruppenaktionen erfordern eine Online-Verbindung</span>
        </div>
      )}
      
      {/* Show error messages */}
      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}
      
      {/* Show success messages */}
      {successMessage && (
        <div className="success-message">
          {successMessage}
          <button onClick={() => setSuccessMessage(null)}>✕</button>
        </div>
      )}
      
      <div className="group-container">
        {/* Left sidebar - Group list */}
        <div className="group-sidebar">
          <h3>Meine Gruppen</h3>
          
          {isLoading && <p className="loading">Lade Gruppen...</p>}
          
          <ul className="group-list">
            {userGroups.map(group => (
              <li 
                key={group.id} 
                className={selectedGroupId === group.id ? 'selected' : ''}
                onClick={() => setSelectedGroupId(group.id)}
              >
                {group.name}
              </li>
            ))}
          </ul>
          
          {userGroups.length === 0 && !isLoading && (
            <p className="empty-state">Du bist noch in keiner Gruppe.</p>
          )}
          
          {/* Create group form */}
          <div className="create-group-section">
            <h4>Neue Gruppe erstellen</h4>
            <form onSubmit={handleCreateGroup}>
              <div className="form-group">
                <label htmlFor="group-name">Gruppenname:</label>
                <input
                  id="group-name"
                  type="text"
                  value={newGroupName}
                  onChange={e => setNewGroupName(e.target.value)}
                  placeholder="Name der Gruppe"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="group-desc">Beschreibung (optional):</label>
                <textarea
                  id="group-desc"
                  value={newGroupDesc}
                  onChange={e => setNewGroupDesc(e.target.value)}
                  placeholder="Kurze Beschreibung der Gruppe"
                  rows={3}
                />
              </div>
              
              <button 
                type="submit" 
                className="primary-button" 
                disabled={isCreatingGroup || !newGroupName.trim() || !isOnline}
                title={!isOnline ? "Internetverbindung erforderlich" : ""}
              >
                {isCreatingGroup ? 'Erstelle...' : 'Gruppe erstellen'}
              </button>
            </form>
          </div>
          
          {/* Join group form */}
          <div className="join-group-section">
            <h4>Gruppe beitreten</h4>
            <form onSubmit={handleJoinGroup}>
              <div className="form-group">
                <label htmlFor="invite-code">Einladungscode:</label>
                <input
                  id="invite-code"
                  type="text"
                  value={joinInviteCode}
                  onChange={e => setJoinInviteCode(e.target.value)}
                  placeholder="Einladungscode eingeben"
                  required
                />
              </div>
              
              <button 
                type="submit" 
                className="primary-button" 
                disabled={isJoiningGroup || !joinInviteCode.trim() || !isOnline}
                title={!isOnline ? "Internetverbindung erforderlich" : ""}
              >
                {isJoiningGroup ? 'Trete bei...' : 'Gruppe beitreten'}
              </button>
            </form>
          </div>
        </div>
        
        {/* Main content - Group details */}
        <div className="group-details">
          {selectedGroup ? (
            <>
              <div className="group-header">
                <h3>{selectedGroup.name}</h3>
                <button 
                  className="danger-button" 
                  onClick={handleLeaveGroup}
                  disabled={isLoading || !isOnline}
                  title={!isOnline ? "Internetverbindung erforderlich" : ""}
                >
                  Gruppe verlassen
                </button>
              </div>
              
              <div className="tabs">
                <button 
                  className={activeTab === 'info' ? 'active' : ''} 
                  onClick={() => setActiveTab('info')}
                >
                  Info
                </button>
                <button 
                  className={activeTab === 'members' ? 'active' : ''} 
                  onClick={() => setActiveTab('members')}
                >
                  Mitglieder
                </button>
                {/* Pictures tab disabled - users should send images in group chat
                <button 
                  className={activeTab === 'pictures' ? 'active' : ''} 
                  onClick={() => setActiveTab('pictures')}
                >
                  Bilder
                </button>
                */}
              </div>
              
              <div className="tab-content">
                {activeTab === 'info' && (
                  <div className="info-tab">
                    <div className="group-info">
                      <p><strong>Erstellt am:</strong> {formatDate(selectedGroup.created_at)}</p>
                      {selectedGroup.description && (
                        <div className="description">
                          <h4>Beschreibung:</h4>
                          <p>{selectedGroup.description}</p>
                        </div>
                      )}
                    </div>
                    
                    <div className="invite-section">
                      <h4>Neue Mitglieder einladen</h4>
                      <p>Generiere einen Einladungscode, um andere zu deiner Gruppe einzuladen.</p>
                      
                      {inviteCode ? (
                        <div className="invite-code-display">
                          <div className="invite-qr-code">
                            <canvas ref={qrCanvasRef}></canvas>
                          </div>
                          <p className="code">{inviteCode}</p>
                          <p className="invite-url">
                            {`${window.location.origin}/invite#${inviteCode}`}
                          </p>
                          <button 
                            className="secondary-button"
                            onClick={copyInviteToClipboard}
                          >
                            Link kopieren
                          </button>
                        </div>
                      ) : (
                        <button 
                          className="primary-button" 
                          onClick={handleGenerateInvite}
                          disabled={isGeneratingInvite || !isOnline}
                          title={!isOnline ? "Internetverbindung erforderlich" : ""}
                        >
                          {isGeneratingInvite ? 'Generiere...' : 'Einladungscode generieren'}
                        </button>
                      )}
                    </div>
                  </div>
                )}
                
                {activeTab === 'members' && (
                  <div className="members-tab">
                    <h4>Gruppenmitglieder</h4>
                    
                    {isLoading && <p className="loading">Lade Mitglieder...</p>}
                    
                    <ul className="members-list">
                      {groupMembers.map(member => (
                        <li key={member.id} className="member-item">
                          <div className="member-info">
                            <span className="username">{member.username}</span>
                            <span className="joined-date">
                              Beigetreten: {formatDate(member.joined_at)}
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                    
                    {!isLoading && groupMembers.length === 0 && (
                      <p className="empty-state">Diese Gruppe hat keine Mitglieder.</p>
                    )}
                  </div>
                )}
                
                {/* Pictures tab disabled - users should send images in group chat instead
                {activeTab === 'pictures' && (
                  <div className="pictures-tab">
                    <div style={{
                      padding: '2rem',
                      textAlign: 'center',
                      backgroundColor: '#e7f3ff',
                      borderRadius: '8px',
                      border: '2px solid #2196F3',
                      margin: '1rem 0'
                    }}>
                      <h4 style={{ marginBottom: '1rem', color: '#1976D2' }}>
                        📸 Bilder-Feature deaktiviert
                      </h4>
                      <p style={{ fontSize: '1rem', lineHeight: '1.6', color: '#333' }}>
                        Die Bild-Upload-Funktion wurde deaktiviert.<br/>
                        Bitte teilt eure Bilder direkt in eurem Gruppenchat!
                      </p>
                      <p style={{ fontSize: '0.9rem', marginTop: '1rem', color: '#666' }}>
                        💬 Nutzt eure bevorzugte Chat-App (WhatsApp, Telegram, etc.) um Bilder mit eurer Gruppe zu teilen.
                      </p>
                    </div>
                  </div>
                )}
                */}
              </div>
            </>
          ) : (
            <div className="no-selection">
              <p>Wähle eine Gruppe aus oder erstelle eine neue Gruppe.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GroupManagement;